import { DebitNote } from '../../types';
import { DebitNoteTemplateContext } from './DebitNoteLetterTemplate';

export const generateArabicTemplate = (
  notes: DebitNote[],
  ctx: DebitNoteTemplateContext
): string => {
  const totalUSD = notes.reduce((sum, note) => sum + note.netRemaining.usd, 0);
  const totalEGP = notes.reduce((sum, note) => sum + note.netRemaining.egp, 0);

  return `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
    <meta charset="UTF-8">
    <title>طلب تسوية مديونية مدينة</title>
    <style>
        @page { margin: 20mm; size: A4; }
        body { font-family: 'Traditional Arabic', 'Tahoma', sans-serif; font-size: 12px; line-height: 1.6; margin: 0; padding: 0; color: #000; direction: rtl; text-align: right; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 15px; }
        .company-name { font-size: 18px; font-weight: bold; color: #000; margin-bottom: 5px; }
        .date-section { text-align: left; margin: 20px 0; font-weight: bold; }
        .recipient-section { margin: 20px 0; padding: 15px; border: 1px solid #000; background-color: #f9f9f9; }
        .subject-line { margin: 20px 0; font-weight: bold; font-size: 14px; text-decoration: underline; }
        .content-section { margin: 20px 0; text-align: justify; }
        .debit-notes-section { margin: 20px 0; padding: 15px; border: 1px solid #000; background-color: #f5f5f5; }
        .debit-notes-title { font-weight: bold; margin-bottom: 15px; font-size: 14px; text-decoration: underline; }
        .debit-note-item { margin: 10px 0; padding: 10px; background-color: white; border-right: 3px solid #007acc; }
        .note-number { font-weight: bold; color: #007acc; }
        .note-details { margin-right: 20px; }
        .total-section { margin: 20px 0; padding: 15px; border: 2px solid #000; background-color: #e0e0e0; text-align: center; }
        .total-amount { font-size: 16px; font-weight: bold; color: #d00; }
        .signature-section { margin-top: 50px; text-align: center; }
        .signature-line { border-bottom: 1px solid #000; margin: 30px auto 10px auto; width: 200px; }
        .footer { margin-top: 40px; text-align: center; border-top: 1px solid #000; padding-top: 15px; font-size: 10px; background-color: #f9f9f9; }
        @media print { .no-print { display: none !important; } }
    </style>
</head>
<body>
    <div class="header">
        <div class="company-name">شركة بتروماري للمشاريع البحرية</div>
        <div style="font-size: 12px; color: #666;">Petromarine Offshore Projects Support Co.</div>
    </div>
    <div class="date-section">
        التاريخ: ${new Date().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })}
    </div>
    <div class="recipient-section">
        <div><strong>إلى:</strong> ${ctx.clientName || '[اسم العميل]'}</div>
        <div style="margin-top: 5px; padding-right: 30px;">${ctx.clientAddress || '[عنوان العميل]'}</div>
    </div>
    <div class="subject-line">الموضوع: طلب تسوية مديونية مدينة</div>
    <div class="content-section">
        <p><strong>السادة المحترمين،</strong></p>
        <p>نأمل أن تكونوا بأفضل حال وأحسن صحة.</p>
        <p>نود إعلامكم بوجود مديونية مدينة متعلقة بـ <strong>الفاتورة رقم ${ctx.invoiceId}</strong> للسفينة <strong>${ctx.vesselName || '[اسم السفينة]'}</strong> والتي تتطلب اهتمامكم العاجل.</p>
    </div>
    <div class="debit-notes-section">
        <div class="debit-notes-title">تفاصيل المديونية المدينة:</div>
        ${notes.map((note, index) => `
            <div class="debit-note-item">
                <div class="note-number">${index + 1}. التاريخ: ${note.date}</div>
                <div class="note-details">
                    <div><strong>الوصف:</strong> ${note.description}</div>
                    <div><strong>المبلغ:</strong> ${note.netRemaining.usd.toFixed(2)} دولار أمريكي / ${note.netRemaining.egp.toFixed(2)} جنيه مصري</div>
                </div>
            </div>
        `).join('')}
    </div>
    <div class="total-section">
        <div class="total-amount">إجمالي المبلغ المستحق: ${totalUSD.toFixed(2)} دولار أمريكي / ${totalEGP.toFixed(2)} جنيه مصري</div>
    </div>
    <div class="content-section">
        <p>نرجو منكم تسوية المبالغ المستحقة المذكورة أعلاه في أسرع وقت ممكن. نود أن نلفت انتباهكم إلى أن هذه المبالغ مستحقة الدفع وفقاً للشروط المتفق عليها.</p>
        <p>في حالة وجود أي استفسارات أو الحاجة إلى مستندات إضافية، يرجى عدم التردد في الاتصال بنا.</p>
        <p>نشكركم لتعاونكم واهتمامكم السريع بهذا الأمر.</p>
    </div>
    <div class="signature-section">
        <p><strong>مع أطيب التحيات،</strong></p>
        <div class="signature-line"></div>
        <div><strong>المدير العام</strong></div>
        <div><strong>شركة بتروماري للمشاريع البحرية</strong></div>
    </div>
    <div class="footer">
        <div><strong>العنوان:</strong> مكتب رقم 101، مبنى رقم 3، شارع رقم 983، طريق صلوى، الإسكندرية، مصر</div>
        <div><strong>تليفون:</strong> +20 1 069 069 300 | <strong>فاكس:</strong> +20 3 569 5300</div>
        <div><strong>البريد الإلكتروني:</strong> operations@petromarine.net | <strong>الموقع الإلكتروني:</strong> www.petromarine.net</div>
    </div>
</body>
</html>`;
};
